import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Axios from "axios";
import { Button, Skeleton, Typography, Input, Tag, Select } from "antd";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import AssetInWallet from "Components/Dashboard/AssetInWallet";
import BinanceBalanceCard from "Components/Dashboard/BinanceBalanceCard";
import AllocationBar from "Components/Dashboard/AllocationBar";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import AddBinance from "Components/Forms/AddBinance";
import {
    ArrowLeftOutlined,
    ReloadOutlined,
    SyncOutlined,
    SearchOutlined,
    BarChartOutlined,
    MessageOutlined,
} from "@ant-design/icons";
import BinancePreferences from "Components/Drawers/BinancePreferences";
import CutoffDateDrawer, {
    formatCutoffLabel,
} from "Components/Drawers/CutoffDateDrawer";
import { updateSettings } from "store/Actions/settings";
import { subscribeToLedgerSync } from "utils/Firebase/ledgerSync";

const { Text } = Typography;

const BINANCE_SORT_OPTIONS = [
    { value: "currentValue", label: "Current value" },
    { value: "investment", label: "Invested" },
    { value: "roi", label: "ROI %" },
    { value: "price", label: "Price" },
    { value: "quantity", label: "Quantity" },
    { value: "name", label: "Name" },
];

const BinancePortfolio = () => {
    document.title = `Binance — Portfolio`;
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settings);

    const urlParams = new URLSearchParams(location.search);
    const urlTimeRange = urlParams.get("days");
    const urlCurrency = urlParams.get("currency");
    const urlSortBy = urlParams.get("sortBy");
    const urlCutoffDate = urlParams.get("cutoffDate");

    const [items, setItems] = useState([]);
    const [overview, setOverview] = useState(0);
    const [investment, setInvestment] = useState(0);
    const [totalRoiValue, setTotalRoiValue] = useState(0);
    const [realizedPl, setRealizedPl] = useState(0);
    const [usdt, setUsdt] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showAICTA, setShowAICTA] = useState(false);
    const [hasConnectedBinance, setHasConnectedBinance] = useState(true);
    const [showConnectBinanceDrawer, setShowConnectBinanceDrawer] =
        useState(false);
    const [sortBy, setSortBy] = useState(
        urlSortBy ||
            settings?.defaultSortBy ||
            settings?.binanceSortBy ||
            "currentValue"
    );
    const [showOptionsDrawer, setShowOptionsDrawer] = useState(false);
    const [currency, setCurrency] = useState(
        urlCurrency || settings?.binanceCurrency || settings?.currency || "USDT"
    );
    const [cutoffDate, setCutoffDate] = useState(
        urlCutoffDate || settings?.binanceCutoffDate || "2026-01-01"
    );
    const [showCutoffDrawer, setShowCutoffDrawer] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [hideSmallBalances, setHideSmallBalances] = useState(
        Boolean(settings?.hideSmallBalances)
    );
    const [ledgerSync, setLedgerSync] = useState(null);

    const getInitialTimeRange = () => {
        if (urlTimeRange) {
            return urlTimeRange === "all" ? "all" : parseInt(urlTimeRange, 10);
        }
        return settings?.binanceTimeRange || 7;
    };
    const [timeRange, setTimeRange] = useState(getInitialTimeRange());
    const [supportedCurrencies, setSupportedCurrencies] = useState([]);

    const CURRENCIES_CACHE_KEY = "binance_supported_currencies";
    const CURRENCIES_CACHE_TTL = 30 * 24 * 60 * 60 * 1000;

    const onClose = () => {
        history.push("/");
    };

    const sortAssets = (coins) => {
        if (sortBy === "investment")
            coins.sort((a, b) => (b.invested || 0) - (a.invested || 0));
        else if (sortBy === "currentValue") {
            coins.sort((a, b) => {
                const valueA = a.currentValue || 0;
                const valueB = b.currentValue || 0;
                return valueB - valueA;
            });
        } else if (sortBy === "name")
            coins.sort((a, b) =>
                (a.name || a.asset || "").localeCompare(b.name || b.asset || "")
            );
        else if (sortBy === "roi")
            coins.sort((a, b) => {
                const roiA = a.roi?.percentage || 0;
                const roiB = b.roi?.percentage || 0;
                if (!Number.isFinite(roiA) && !Number.isFinite(roiB)) return 0;
                if (!Number.isFinite(roiA)) return 0;
                if (!Number.isFinite(roiB)) return 0;
                if (Number.isNaN(roiA) && Number.isNaN(roiB)) return 0;
                if (Number.isNaN(roiA)) return 0;
                if (Number.isNaN(roiB)) return 0;
                return roiB - roiA;
            });
        else if (sortBy === "price")
            coins.sort((a, b) => (b.currentPrice || 0) - (a.currentPrice || 0));
        else if (sortBy === "quantity")
            coins.sort((a, b) => (b.quantity || 0) - (a.quantity || 0));

        setItems([...coins]);
    };

    const getAssetInfo = async (options = {}) => {
        const { silent = false } = options || {};
        if (!silent) {
            setOverview(0);
            setInvestment(0);
            setTotalRoiValue(0);
            setRealizedPl(0);
            setLoading(true);
        }
        try {
            const api = `${process.env.REACT_APP_API}/binance/wallet`;
            const response = await Axios.get(api, {
                params: {
                    days: timeRange === "all" ? "all" : timeRange,
                    currency,
                    cutoffDate: cutoffDate || "2026-01-01",
                },
                withCredentials: true,
            });
            const syncMeta = response.data?.meta?.ledgerSync || null;
            setLedgerSync(syncMeta);
            const { data } = response.data;
            const { assets: walletAssets, summary } = data || {};

            const assets = [];
            const currencyAsset = currency.toUpperCase();

            let usdtBalanceValue = 0;
            if (walletAssets && Array.isArray(walletAssets)) {
                const usdtAsset = walletAssets.find(
                    (assetData) => assetData.name === "USDT"
                );
                if (usdtAsset) {
                    usdtBalanceValue = usdtAsset.currentValue || 0;
                } else {
                    usdtBalanceValue = summary?.usdtBalance || 0;
                }
            } else {
                usdtBalanceValue = summary?.usdtBalance || 0;
            }
            setUsdt(usdtBalanceValue);

            let sumInvested = 0;
            let sumOverview = 0;
            if (walletAssets && Array.isArray(walletAssets)) {
                walletAssets.forEach((assetData) => {
                    if (
                        assetData.name !== "USDT" &&
                        assetData.name !== currencyAsset
                    ) {
                        if ((assetData.currentValue || 0) > 0) {
                            assets.push(assetData);
                            sumInvested += Number(assetData.invested) || 0;
                            sumOverview += Number(assetData.currentValue) || 0;
                        }
                    }
                });
            }
            const netInvested = sumInvested;
            const netRoiValue = sumOverview - netInvested;
            const realizedPlValue = Number.isFinite(Number(summary?.realizedPL))
                ? Number(summary.realizedPL)
                : assets.reduce(
                      (acc, a) => acc + (Number(a.realizedPL) || 0),
                      0
                  );
            setInvestment(netInvested);
            setOverview(sumOverview);
            setTotalRoiValue(netRoiValue);
            setRealizedPl(realizedPlValue);
            setItems(assets);
            sortAssets(assets);
            setShowAICTA(true);
            setHasConnectedBinance(true);
            setShowConnectBinanceDrawer(false);
            try {
                localStorage.setItem(
                    "binance_wallet_summary_cache",
                    JSON.stringify({
                        totalValue: sumOverview,
                        cryptoValue: sumOverview,
                        usdtBalance: usdtBalanceValue,
                        invested: netInvested,
                        totalRoiValue: netRoiValue,
                        realizedPl: realizedPlValue,
                        cutoffDate: cutoffDate || "2026-01-01",
                        roiPct:
                            netInvested > 0
                                ? (netRoiValue / netInvested) * 100
                                : 0,
                        assetCount: assets.length,
                        currency,
                        updatedAt: Date.now(),
                    })
                );
            } catch (storageErr) {
                // ignore storage errors
            }
        } catch (error) {
            if (error.response?.status === 403) {
                setShowConnectBinanceDrawer(true);
                setHasConnectedBinance(false);
            }
        }
        if (!silent) {
            setLoading(false);
        }
    };

    const handleNewsClick = () => history.push("/imported/binance/ai-news");

    const fetchSupportedCurrencies = async () => {
        try {
            const cached = localStorage.getItem(CURRENCIES_CACHE_KEY);
            if (cached) {
                const { data, timestamp } = JSON.parse(cached);
                const now = Date.now();
                if (now - timestamp < CURRENCIES_CACHE_TTL) {
                    setSupportedCurrencies(data);
                    return;
                }
            }

            const api = `${process.env.REACT_APP_API}/coin-gecko/supported-vs-currencies`;
            const response = await Axios.get(api);

            if (response.data && response.data.data) {
                const formatted = response.data.data.map((c) =>
                    c.toUpperCase()
                );
                localStorage.setItem(
                    CURRENCIES_CACHE_KEY,
                    JSON.stringify({ data: formatted, timestamp: Date.now() })
                );
                setSupportedCurrencies(formatted);
            }
        } catch (error) {
            setSupportedCurrencies(["USDT", "USD", "INR"]);
        }
    };

    useEffect(() => {
        if (settings?.hideSmallBalances !== undefined) {
            setHideSmallBalances(Boolean(settings.hideSmallBalances));
        }
    }, [settings?.hideSmallBalances]);

    useEffect(() => {
        if (
            !urlSortBy &&
            (settings?.defaultSortBy || settings?.binanceSortBy)
        ) {
            setSortBy(settings.defaultSortBy || settings.binanceSortBy);
        }
    }, [urlSortBy, settings?.defaultSortBy, settings?.binanceSortBy]);

    useEffect(() => {
        if (!urlCurrency && (settings?.binanceCurrency || settings?.currency)) {
            setCurrency(settings.binanceCurrency || settings.currency);
        }
    }, [urlCurrency, settings?.binanceCurrency, settings?.currency]);

    useEffect(() => {
        if (!urlCutoffDate && settings?.binanceCutoffDate) {
            setCutoffDate(settings.binanceCutoffDate);
        }
    }, [urlCutoffDate, settings?.binanceCutoffDate]);

    useEffect(() => {
        if (hasConnectedBinance) {
            fetchSupportedCurrencies();
            getAssetInfo();
        }
    }, [currency, timeRange, cutoffDate]);

    useEffect(() => {
        if (hasConnectedBinance) sortAssets(items);
    }, [sortBy]);

    useEffect(() => {
        if (!hasConnectedBinance || ledgerSync?.status !== "syncing") {
            return undefined;
        }
        const unsubscribe = subscribeToLedgerSync(
            ledgerSync.userId || "default",
            (docData) => {
                if (
                    docData?.status === "ready" ||
                    docData?.status === "error"
                ) {
                    getAssetInfo({ silent: true });
                }
            }
        );
        const fallbackTimer = setTimeout(() => {
            getAssetInfo({ silent: true });
        }, 6000);
        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            }
            clearTimeout(fallbackTimer);
        };
    }, [
        hasConnectedBinance,
        ledgerSync?.status,
        ledgerSync?.userId,
        currency,
        timeRange,
        cutoffDate,
    ]);

    const filteredItems = items.filter((item) => {
        const symbol = (item.name || item.asset || "").toLowerCase();
        if (searchQuery && !symbol.includes(searchQuery.toLowerCase())) {
            return false;
        }
        if (hideSmallBalances && (item.currentValue || 0) < 1.0) {
            return false;
        }
        if (categoryFilter === "earn" && !(item.allocation?.earn > 0)) {
            return false;
        }
        if (categoryFilter === "spot" && !(item.allocation?.spot > 0)) {
            return false;
        }
        if (categoryFilter === "funding" && !(item.allocation?.funding > 0)) {
            return false;
        }
        if (categoryFilter === "gainers" && (item.roi?.percentage || 0) <= 0) {
            return false;
        }
        if (categoryFilter === "losers" && (item.roi?.percentage || 0) >= 0) {
            return false;
        }
        return true;
    });

    const categoryChips = [
        { key: "all", label: "All assets" },
        { key: "earn", label: "Earn" },
        { key: "spot", label: "Spot" },
        { key: "funding", label: "Funding" },
        { key: "gainers", label: "Gainers" },
        { key: "losers", label: "Losers" },
    ];

    return (
        <div>
            <AppShell
                activeTab="binance"
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                            width: "100%",
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                minWidth: 0,
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ArrowLeftOutlined
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "16px",
                                        }}
                                    />
                                }
                                onClick={onClose}
                                title="Back to portfolios"
                                style={{
                                    padding: 0,
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            />
                            <Typography.Title
                                level={4}
                                style={{
                                    color: Tokens.textPrimary,
                                    margin: 0,
                                    fontWeight: 500,
                                    fontSize: "18px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Binance
                            </Typography.Title>
                            <AutoSyncBadge />
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                                flexShrink: 0,
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ReloadOutlined
                                        spin={loading}
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "16px",
                                        }}
                                    />
                                }
                                onClick={() => getAssetInfo()}
                                title="Refresh assets"
                                style={{
                                    backgroundColor: Tokens.raised,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusControl,
                                    width: "36px",
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            />
                        </div>
                    </div>
                }
                placement="right"
                closable={false}
                push={false}
                height="100%"
                width="100%"
                open
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                    padding: "16px 24px",
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "24px 16px 80px",
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding:
                        "10px 16px calc(10px + env(safe-area-inset-bottom, 0px))",
                    textAlign: "left",
                }}
                footer={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                display: loading ? "none" : "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "12px",
                                width: "100%",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "2px",
                                    minWidth: 0,
                                }}
                            >
                                <span
                                    style={{
                                        color: Tokens.textMuted,
                                        fontSize: "11px",
                                        lineHeight: "1.2",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Liquid cash
                                </span>
                                <Text
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontWeight: 600,
                                        fontSize: "15px",
                                        lineHeight: "1.3",
                                        fontFamily: Tokens.fontMono,
                                        fontVariantNumeric: "tabular-nums",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {getCurrencySymbol(currency)}
                                    {getFormattedAmount(usdt, true, 2)}
                                </Text>
                            </div>

                            <Button
                                type="default"
                                icon={
                                    <MessageOutlined
                                        style={{ color: Tokens.textSecondary }}
                                    />
                                }
                                onClick={handleNewsClick}
                                style={{
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairlineStrong}`,
                                    borderRadius: Tokens.radiusControl,
                                    fontWeight: 500,
                                    fontSize: "13px",
                                    height: "38px",
                                    padding: "0 14px",
                                    flexShrink: 0,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Ask AI assistant
                            </Button>
                        </div>
                    </div>
                }
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    {loading && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                            }}
                        >
                            {[1, 2, 3, 4].map((index) => (
                                <div
                                    key={index}
                                    style={{
                                        backgroundColor: Tokens.card,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusCard,
                                        padding: "20px",
                                    }}
                                >
                                    <Skeleton active paragraph={{ rows: 1 }} />
                                </div>
                            ))}
                        </div>
                    )}
                    {!loading && (
                        <AddBinance
                            onClose={() => setShowConnectBinanceDrawer(false)}
                            showConnectBinanceDrawer={showConnectBinanceDrawer}
                            getAssetInfo={getAssetInfo}
                        />
                    )}
                    {!loading && items.length > 0 && (
                        <React.Fragment>
                            {ledgerSync?.status === "syncing" && (
                                <div
                                    data-testid="binance-ledger-sync-banner"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: "12px",
                                        backgroundColor:
                                            "rgba(240, 185, 11, 0.08)",
                                        border: "1px solid rgba(240, 185, 11, 0.28)",
                                        borderRadius: Tokens.radiusControl,
                                        padding: "10px 14px",
                                        marginBottom: "16px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            minWidth: 0,
                                        }}
                                    >
                                        <SyncOutlined
                                            spin
                                            style={{
                                                color: Tokens.accent,
                                                fontSize: "14px",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Text
                                            style={{
                                                color: Tokens.textPrimary,
                                                fontSize: "12px",
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            Balance update detected on Binance —
                                            syncing latest trades in the
                                            background to refresh average price
                                            and P&amp;L…
                                        </Text>
                                    </div>
                                </div>
                            )}

                            <BinanceBalanceCard
                                current={overview}
                                invested={investment}
                                netPl={totalRoiValue}
                                realizedPl={realizedPl}
                                roi={
                                    investment > 0
                                        ? (totalRoiValue / investment) * 100
                                        : 0
                                }
                                currency={currency}
                                cutoffLabel={formatCutoffLabel(cutoffDate)}
                                onOpenCutoffDrawer={() =>
                                    setShowCutoffDrawer(true)
                                }
                            />

                            <AllocationBar items={items} total={overview} />

                            <Button
                                block
                                type="default"
                                icon={
                                    <BarChartOutlined
                                        style={{ color: Tokens.accent }}
                                    />
                                }
                                onClick={() =>
                                    history.push(
                                        "/imported/binance/report-card"
                                    )
                                }
                                style={{
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairlineStrong}`,
                                    boxShadow: Tokens.topHighlight,
                                    borderRadius: Tokens.radiusControl,
                                    height: "44px",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    marginBottom: "16px",
                                }}
                            >
                                Account Summary
                            </Button>

                            <div style={{ marginBottom: "16px" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                        marginBottom: "12px",
                                    }}
                                >
                                    <Input
                                        prefix={
                                            <SearchOutlined
                                                style={{
                                                    color: Tokens.textMuted,
                                                }}
                                            />
                                        }
                                        placeholder="Search assets"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                        style={{
                                            flex: 1,
                                            backgroundColor: Tokens.card,
                                            borderColor: Tokens.hairline,
                                            color: Tokens.textPrimary,
                                            borderRadius: Tokens.radiusControl,
                                            height: "40px",
                                        }}
                                    />
                                    <Select
                                        aria-label="Sort assets"
                                        data-testid="binance-sort-select"
                                        value={sortBy}
                                        onChange={(val) => setSortBy(val)}
                                        options={BINANCE_SORT_OPTIONS}
                                        style={{ width: 156, height: "40px" }}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        overflowX: "auto",
                                        paddingBottom: "4px",
                                    }}
                                >
                                    {categoryChips.map((chip) => {
                                        const isActive =
                                            categoryFilter === chip.key;
                                        return (
                                            <Tag
                                                key={chip.key}
                                                onClick={() =>
                                                    setCategoryFilter(chip.key)
                                                }
                                                style={{
                                                    backgroundColor: isActive
                                                        ? Tokens.raised
                                                        : "transparent",
                                                    color: isActive
                                                        ? Tokens.textPrimary
                                                        : Tokens.textSecondary,
                                                    border: isActive
                                                        ? `1px solid ${Tokens.accent}`
                                                        : `1px solid ${Tokens.hairline}`,
                                                    borderRadius:
                                                        Tokens.radiusTag,
                                                    padding: "4px 12px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: isActive
                                                        ? 500
                                                        : 400,
                                                    userSelect: "none",
                                                    margin: 0,
                                                }}
                                            >
                                                {chip.label}
                                            </Tag>
                                        );
                                    })}
                                </div>
                            </div>

                            {filteredItems.map((asset) => (
                                <div
                                    key={`binance-asset-${
                                        asset.name || asset.asset
                                    }-${asset.currentValue || 0}`}
                                    style={{
                                        marginBottom: "8px",
                                    }}
                                >
                                    <AssetInWallet
                                        asset={asset}
                                        index={asset.name || asset.asset}
                                        total={overview}
                                        investment={investment}
                                        sortBy={sortBy}
                                        currency={currency}
                                        timeRange={timeRange}
                                        cutoffDate={cutoffDate}
                                    />
                                </div>
                            ))}
                        </React.Fragment>
                    )}
                    {!loading && items.length === 0 && showAICTA && (
                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "32px 24px",
                                textAlign: "center",
                            }}
                        >
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                }}
                            >
                                No holdings found on Binance
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "14px",
                                }}
                            >
                                Spot, Earn, and Funding balances will appear
                                here once funded.
                            </div>
                        </div>
                    )}
                    {!loading && items.length === 0 && !showAICTA && (
                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "32px 24px",
                                textAlign: "center",
                            }}
                        >
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                }}
                            >
                                Could not load Binance holdings
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "14px",
                                    marginBottom: "16px",
                                }}
                            >
                                Check your API key connection or refresh in a
                                moment.
                            </div>
                            <Button
                                type="primary"
                                onClick={() => getAssetInfo()}
                            >
                                Retry
                            </Button>
                        </div>
                    )}
                </div>
            </AppShell>
            <BinancePreferences
                onClose={() => setShowOptionsDrawer(false)}
                open={showOptionsDrawer}
                setSortBy={setSortBy}
                sortBy={sortBy}
                currency={currency}
                setCurrency={setCurrency}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                cutoffDate={cutoffDate}
                setCutoffDate={setCutoffDate}
                supportedCurrencies={supportedCurrencies}
                hasConnectedBinance={hasConnectedBinance}
                hideSmallBalances={hideSmallBalances}
                setHideSmallBalances={setHideSmallBalances}
                onOpenUpdateDrawer={() => setShowConnectBinanceDrawer(true)}
                onDisconnectSuccess={() => getAssetInfo()}
            />
            <CutoffDateDrawer
                open={showCutoffDrawer}
                onClose={() => setShowCutoffDrawer(false)}
                value={cutoffDate}
                onChange={(val) => {
                    setCutoffDate(val);
                    dispatch(
                        updateSettings({
                            binanceCutoffDate: val,
                        })
                    );
                }}
            />
        </div>
    );
};

export default BinancePortfolio;
